import React, { useMemo, useState } from 'react';
import { Button, Icon, Image, Loader } from 'semantic-ui-react';
import { format, isToday, isYesterday } from 'date-fns';
import api from '../utils/api';
import './ChangeHistory.css';

/**
 * Readable change history: who changed what, when, with each change shown as Before | After.
 * Entries come from the backend's History collection (already decoded: people by name, fields by label).
 */

export const KINDS = [
	{ key: 'edit', label: 'Edits', icon: 'pencil' },
	{ key: 'notification', label: 'Notifications', icon: 'send' },
	{ key: 'confirmation', label: 'Replies', icon: 'check circle outline' },
];

const VIA = {
	editor: 'Event editor',
	planner: 'Planner',
	services: 'Services page',
	confirmation: 'Reply',
	notification: 'Alert sent',
	blockout: 'Blockout auto-decline',
	'auto-create': 'Auto-create from template',
	'auto-rotation': 'Rotation after a decline',
	'auto-correct': 'Rotation auto-correct',
	'group-sync': 'Group change',
	'template-sync': 'Template change',
};

const COLLECTION_NOUN = { Events: 'event', EventTemplates: 'template', Groups: 'group', Members: 'profile' };

/** changes of the selected kinds (create / delete entries count as edits) */
export const visibleChanges = (entry, kinds) => (entry.changes ?? []).filter(c => kinds.includes(c.kind));
export const isEntryVisible = (entry, kinds) =>
	entry.action !== 'update' ? kinds.includes('edit') : visibleChanges(entry, kinds).length > 0;

const dayLabel = (d) => isToday(d) ? 'Today' : isYesterday(d) ? 'Yesterday' : format(d, 'EEEE, MMM d, yyyy');

export const targetLabel = (entry) => {
	if (entry.collection === 'Events') {
		const start = entry.eventStart ? new Date(entry.eventStart) : undefined;
		return `${start ? format(start, 'EEE M/d h:mm a') + ' ' : ''}${entry.title ?? 'event'}`;
	}
	return entry.title ?? COLLECTION_NOUN[entry.collection];
};

/** "edited", "replied", "created" ... from what the (visible) changes are */
function verb(entry, changes) {
	if (entry.action === 'create') return 'created';
	if (entry.action === 'delete') return 'deleted';
	const kinds = new Set(changes.map(c => c.kind));
	if (kinds.size === 1 && kinds.has('confirmation')) return 'replied';
	if (kinds.size === 1 && kinds.has('notification')) {
		const sent = changes.flatMap(c => c.after ?? []).join(' ');
		const texts = /Texted/.test(sent), emails = /Emailed/.test(sent);
		return texts && emails ? 'sent texts and emails' : emails ? 'sent emails' : 'sent texts';
	}
	return 'edited';
}

function Who({ entry }) {
	if (entry.authorName) {
		return (
			<span className='ch-who'>
				<Image avatar src={entry.authorAvatar || api.getInitialsImg(entry.authorName)} className='ch-avatar' alt='' />
				<strong>{entry.authorName}</strong>
			</span>
		);
	}
	return (
		<span className='ch-who'>
			<span className='ch-avatar ch-avatar-system'><Icon name='cog' fitted /></span>
			<strong>System</strong>
		</span>
	);
}

/** marks which items of `a` are not in `b` (multiset: duplicate names count separately) */
const missingFrom = (a, b) => {
	const rest = [...b];
	const out = new Set();
	a.forEach((x, i) => {
		const j = rest.indexOf(x);
		if (j >= 0) rest.splice(j, 1);
		else out.add(i);
	});
	return out;
};

const COLLAPSE_UNCHANGED = 6;

/** list values: removed items struck through on the left, added items highlighted on the right */
function ListDiff({ before = [], after = [] }) {
	const removed = missingFrom(before, after);
	const added = missingFrom(after, before);
	const unchanged = before.length - removed.size;
	// long lists where only a few people changed (e.g. Attending): show the changes, fold the rest
	const foldable = unchanged > COLLAPSE_UNCHANGED;
	const [showAll, setShowAll] = useState(false);
	const fold = foldable && !showAll;

	const side = (items, changed, cls, emptyText) => {
		const shown = items.map((x, i) => [x, i]).filter(([, i]) => !fold || changed.has(i));
		return (
			<>
				{items.length === 0 && <span className='ch-empty'>{emptyText}</span>}
				{shown.map(([x, i]) => <span key={i} className={`ch-chip ${changed.has(i) ? cls : ''}`}>{x}</span>)}
				{fold && <button type='button' className='ch-fold' onClick={() => setShowAll(true)}>+{unchanged} unchanged</button>}
			</>
		);
	};

	return (
		<>
			<div className='ch-cell ch-before' data-label='Before'>{side(before, removed, 'ch-removed', 'None')}</div>
			<div className='ch-cell ch-after' data-label='After'>
				{side(after, added, 'ch-added', 'None')}
				{foldable && showAll && <button type='button' className='ch-fold' onClick={() => setShowAll(false)}>Hide unchanged</button>}
			</div>
		</>
	);
}

/** replies are colored by what they mean (accepted / declined / no answer), not by before / after */
const replyClass = (text) => /^Accepted/.test(text) ? 'ch-reply-yes' : /^Declined/.test(text) ? 'ch-reply-no' : 'ch-reply-none';

function ValueDiff({ before, after, kind }) {
	const cls = (x, side) => kind === 'confirmation' ? replyClass(x) : side;
	return (
		<>
			<div className='ch-cell ch-before' data-label='Before'>
				{before?.length ? before.map((x, i) => <div key={i} className={`ch-text ${cls(x, 'ch-removed-text')}`}>{x}</div>) : <span className='ch-empty'>Not set</span>}
			</div>
			<div className='ch-cell ch-after' data-label='After'>
				{after?.length ? after.map((x, i) => <div key={i} className={`ch-text ${cls(x, 'ch-added-text')}`}>{x}</div>) : <span className='ch-empty'>Removed</span>}
			</div>
		</>
	);
}

function ChangeRow({ change, useRoles }) {
	const label = useRoles && change.roles?.length ? change.roles.join(', ') : change.label;
	const fieldNote = useRoles && change.roles?.length && change.label ? change.label : undefined;
	const kind = KINDS.find(k => k.key === change.kind);
	return (
		<div className='ch-row'>
			<div className='ch-cell ch-what'>
				<div className='ch-label'>
					{change.kind !== 'edit' && <Icon name={kind?.icon} className={`ch-kind-icon ch-kind-${change.kind}`} />}
					{label}
				</div>
				{(change.detail || fieldNote) &&
					<div className='ch-detail'>{[change.detail, fieldNote].filter(Boolean).join(' in ')}</div>}
			</div>
			{change.list ? <ListDiff before={change.before} after={change.after} /> : <ValueDiff before={change.before} after={change.after} kind={change.kind} />}
		</div>
	);
}

function EntryDetails({ entry, changes, useRoles }) {
	if (entry.action === 'delete') return <div className='ch-note'>The {COLLECTION_NOUN[entry.collection] ?? 'record'} was deleted.</div>;
	if (changes.length === 0) return <div className='ch-note'>Nothing else to show for this change.</div>;

	const sections = [];
	changes.forEach(c => {
		const name = c.eventLabel && entry.batchDocIds?.length > 1 ? c.section : useRoles ? (c.section === 'Details' ? 'Details' : 'Services roles') : c.section;
		let s = sections.find(x => x.name === name);
		if (!s) sections.push(s = { name, changes: [] });
		s.changes.push(c);
	});

	return (
		<div className='ch-details'>
			{sections.map(s => (
				<section key={s.name} className='ch-section'>
					<h5 className='ch-section-title'>{s.name}</h5>
					<div className='ch-grid'>
						<div className='ch-row ch-head' aria-hidden='true'>
							<div className='ch-cell'>Changed</div>
							<div className='ch-cell'>Before</div>
							<div className='ch-cell'>After</div>
						</div>
						{s.changes.map((c, i) => <ChangeRow key={i} change={c} useRoles={useRoles} />)}
					</div>
				</section>
			))}
		</div>
	);
}

const clip = (text, max = 40) => text && text.length > max ? `${text.slice(0, max - 1)}…` : text;
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
/** characters (about two lines) on the collapsed row before "+N more"; smaller on phones */
const previewBudget = () => (typeof window !== 'undefined' && window.innerWidth < 600 ? 110 : 220);

/**
 * Old → new for long texts: when both start the same (a long description edited near its end), skip the shared
 * start so the part that changed shows: "…choir first, then prayer" → "…choir first, then communion".
 */
export function diffSnippet(before, after, max = 40) {
	if (before === undefined || after === undefined || (before.length <= max && after.length <= max)) return [clip(before, max), clip(after, max)];
	let p = 0;
	while (p < before.length && p < after.length && before[p] === after[p]) p++;
	const start = p > 12 ? p - 12 : 0;
	const cut = (t) => `${start > 0 ? '…' : ''}${t.slice(start, start + max)}${t.length > start + max ? '…' : ''}`;
	return [cut(before), cut(after)];
}

/** "A, B +3" for long name lists */
const names = (list, max = 2) => list.length > max ? `${list.slice(0, max).join(', ')} +${list.length - max}` : list.join(', ');
const diffLists = (before = [], after = []) => {
	const rest = [...after];
	const removed = [];
	before.forEach(x => { const i = rest.indexOf(x); if (i >= 0) rest.splice(i, 1); else removed.push(x); });
	return { removed, added: rest };
};

/**
 * Collapsed-row preview items: as much of the actual change as fits (people old → new, values old → new, replies
 * ✓ / ✗, who was texted). Each item: { kind, parts: [{ text, tone? }], icon?, tone? }; tone 'old' | 'new' | 'yes' | 'no'.
 */
export function previewItems(entry, changes, useRoles) {
	if (entry.action === 'delete' || changes.length === 0) return [];
	const nameOf = (c) => useRoles && c.roles?.length ? c.roles.join(', ') : c.label;
	const items = [];

	// Attending people changes that only mirror another field's people (automatic sync) are left to the expanded view
	const edits = changes.filter(c => c.kind === 'edit');
	const otherPeople = new Set(edits.filter(c => c.list && c.section !== 'Attendance').flatMap(c => { const d = diffLists(c.before, c.after); return [...d.removed, ...d.added]; }));
	const mirrorsOthers = (c) => {
		if (c.section !== 'Attendance' || !c.list) return false;
		const d = diffLists(c.before, c.after);
		return [...d.removed, ...d.added].every(n => otherPeople.has(n));
	};

	edits.filter(c => !mirrorsOthers(c)).forEach(c => {
		const label = nameOf(c);
		if (c.detail === 'Field added') return items.push({ kind: 'edit', parts: [{ text: `+ ${label}` }, ...(c.after?.length ? [{ text: ': ' }, { text: names(c.after), tone: 'new' }] : [])] });
		if (c.detail === 'Field removed') return items.push({ kind: 'edit', parts: [{ text: `− ${label}`, tone: 'old' }] });
		if (c.detail === 'Rotation') return items.push({ kind: 'edit', parts: [{ text: `${label} rotation updated` }] });
		const prefix = !c.detail || c.detail === 'People' ? label : `${label} ${c.detail.toLowerCase()}`;
		if (c.list) {
			const { removed, added } = diffLists(c.before, c.after);
			const parts = [{ text: `${prefix}: ` }];
			if (removed.length) parts.push({ text: names(removed), tone: 'old' });
			if (removed.length && added.length) parts.push({ text: ' → ' });
			if (added.length) parts.push({ text: `${removed.length ? '' : '+ '}${names(added)}`, tone: 'new' });
			return items.push({ kind: 'edit', parts });
		}
		const [oldText, newText] = diffSnippet(c.before?.[0], c.after?.[0]);
		items.push({ kind: 'edit', parts: [
			{ text: `${prefix}: ` },
			{ text: oldText ?? '—', tone: c.before?.length ? 'old' : undefined },
			{ text: ' → ' },
			{ text: newText ?? '—', tone: c.after?.length ? 'new' : undefined },
		] });
	});

	changes.filter(c => c.kind === 'confirmation').forEach(c => {
		const answer = c.after?.[0] ?? '';
		const tone = /^Accepted/.test(answer) ? 'yes' : /^Declined/.test(answer) ? 'no' : 'none';
		const someoneElse = c.detail && c.detail !== entry.authorName;
		const reason = /\(/.test(answer) ? ` ${answer.replace(/^(Accepted|Declined)\s*/, '')}` : '';
		items.push({ kind: 'confirmation', tone, icon: tone === 'yes' ? 'check' : tone === 'no' ? 'times' : 'circle outline',
			parts: [{ text: `${someoneElse ? `${c.detail}: ` : ''}${nameOf(c)}` }, ...(reason ? [{ text: reason, tone: 'reason' }] : [])] });
	});

	const sent = changes.filter(c => c.kind === 'notification');
	if (sent.length) {
		// one message per person and send time, even when it covers several of their fields
		// one message per person, event and send time (a person texted for two services got two texts)
		const messages = (re) => new Set(sent.filter(c => re.test(c.after?.[0] ?? '')).map(c => `${c.eventLabel ?? ''}|${c.personId ?? c.detail}|${c.after?.[0]}`)).size;
		const texts = messages(/^Texted/), emails = messages(/^Emailed/);
		const people = [...new Set(sent.map(c => c.detail ?? c.personId))];
		const allFields = [...new Set(sent.flatMap(c => useRoles && c.roles?.length ? c.roles : (c.fields?.length ? c.fields : [c.label])))];
		const fields = allFields.length > 4 ? [...allFields.slice(0, 4), `+${allFields.length - 4}`] : allFields;
		const counts = [texts && plural(texts, 'text', 'texts'), emails && plural(emails, 'email', 'emails')].filter(Boolean).join(' + ');
		const who = people.length <= 3 && people.join(', ').length <= 60 ? people.join(', ') : plural(people.length, 'person', 'people');
		items.push({ kind: 'notification', icon: 'send', parts: [{ text: `${counts} to ${who} · ${fields.join(', ')}` }] });
	}
	return items;
}

/** The 2nd line of a collapsed row: as many preview items as fit, then "+N more" (expand for everything) */
function Preview({ entry, changes, useRoles }) {
	const items = previewItems(entry, changes, useRoles);
	if (items.length === 0) return null;
	const shown = [];
	const budget = previewBudget();
	let used = 0;
	for (const item of items) {
		const len = item.parts.reduce((n, p) => n + p.text.length, 0) + 3;
		if (shown.length > 0 && used + len > budget) break;
		shown.push(item);
		used += len;
	}
	const more = items.length - shown.length;
	return (
		<span className='ch-preview'>
			{shown.map((item, i) => (
				<span key={i} className={`ch-pv-item ${item.tone ? `ch-pv-${item.tone}` : ''}`}>
					{item.icon && <Icon name={item.icon} className={item.kind === 'notification' ? 'ch-kind-icon ch-kind-notification' : undefined} />}
					{item.parts.map((p, j) => <span key={j} className={p.tone ? `ch-pv-${p.tone}` : undefined}>{p.text}</span>)}
				</span>
			))}
			{more > 0 && <span className='ch-pv-item ch-pv-more'>+{more} more</span>}
		</span>
	);
}

/**
 * What lists show (both collapsed and expanded):
 * - a person who answered / was texted for a real role doesn't also get an "Attending" line (implied)
 * - texts / emails of one send action (a Services save, Send SMS, one hourly alerts run: same runId) become one row
 *   ("20 texts to 20 people"); with acrossRecords (Dashboard, Services panel, profile) a run over many events is one
 *   row, its expanded view grouped by event. Entries recorded before run ids existed only group per event within
 *   15 minutes. Separate actions never merge, whatever the time between them.
 */
const BATCH_WINDOW_MS = 15 * 60 * 1000;
export function prepareEntries(entries, { acrossRecords = false } = {}) {
	const tidy = (entry) => {
		const roleKinds = new Set((entry.changes ?? [])
			.filter(c => c.kind !== 'edit' && c.section !== 'Attendance' && c.personId)
			.map(c => `${c.kind}|${c.personId}`));
		const changes = (entry.changes ?? []).filter(c => !(c.kind !== 'edit' && c.section === 'Attendance' && roleKinds.has(`${c.kind}|${c.personId}`)));
		return changes.length === (entry.changes ?? []).length ? entry : { ...entry, changes };
	};
	const isSendOnly = (e) => e.action === 'update' && e.kinds?.length === 1 && e.kinds[0] === 'notification';
	const tag = (entry) => entry.changes.map(c => ({ ...c, eventLabel: c.eventLabel ?? targetLabel(entry) }));

	const out = [];
	for (const raw of entries) {
		const entry = tidy(raw);
		const last = out[out.length - 1];
		const sameRun = last && last.runId && entry.runId
			? last.runId === entry.runId && last.authorId === entry.authorId && last.collection === entry.collection && (acrossRecords || last.docId === entry.docId)
			: last && !last.runId && !entry.runId && last.authorId === entry.authorId && last.collection === entry.collection && last.docId === entry.docId
				&& Math.abs(new Date(last.batchStart ?? last.time) - new Date(entry.time)) <= BATCH_WINDOW_MS;
		if (last && sameRun && isSendOnly(last) && isSendOnly(entry)) {
			const docIds = new Set([...(last.batchDocIds ?? [last.docId]), entry.docId]);
			out[out.length - 1] = {
				...last,
				changes: [...tag(last), ...tag(entry)],
				version: docIds.size > 1 ? undefined : Math.max(last.version ?? 0, entry.version ?? 0) || last.version,
				batchStart: last.batchStart ?? last.time,
				batchCount: (last.batchCount ?? 1) + 1,
				batchDocIds: [...docIds],
			};
			continue;
		}
		out.push(entry);
	}
	// a batch over several events: expanded view grouped by event
	return out.map(e => e.batchDocIds?.length > 1
		? { ...e, changes: e.changes.map(c => ({ ...c, section: c.eventLabel })) }
		: e);
}

function Entry({ entry, kinds, showTarget, useRoles, onOpenTarget, defaultOpen }) {
	const [open, setOpen] = useState(!!defaultOpen);
	const changes = visibleChanges(entry, kinds);
	const time = new Date(entry.time);
	const via = VIA[entry.via];
	const hasMeta = via || entry.version !== undefined || (entry.service && entry.service !== entry.title) || entry.batchCount > 1;

	return (
		<li className={`ch-entry ${open ? 'ch-open' : ''}`}>
			<button type='button' className='ch-summary' aria-expanded={open} onClick={() => setOpen(!open)}>
				<span className='ch-time'>{format(time, 'h:mm a')}</span>
				<span className='ch-main'>
					<span className='ch-headline'>
						<Who entry={entry} />
						<span className='ch-verb'>{verb(entry, changes)}</span>
						{showTarget && (entry.batchDocIds?.length > 1
							? <span className='ch-target'>for{' '}<strong>{entry.batchDocIds.length} {COLLECTION_NOUN[entry.collection]}s</strong></span>
							: <span className='ch-target'>{COLLECTION_NOUN[entry.collection]}{' '}<strong>{targetLabel(entry)}</strong></span>)}
					</span>
					<Preview entry={entry} changes={changes} useRoles={useRoles} />
				</span>
				<Icon name={open ? 'chevron up' : 'chevron down'} className='ch-chevron' />
			</button>
			{open &&
				<div className='ch-body'>
					{hasMeta &&
						<div className='ch-meta'>
							{via && <span className='ch-tag'>{via}</span>}
							{entry.version !== undefined && <span className='ch-tag ch-tag-quiet'>Version {entry.version}</span>}
							{entry.service && entry.service !== entry.title && <span className='ch-tag ch-tag-quiet'>{entry.service}</span>}
							{entry.batchCount > 1 && <span className='ch-tag ch-tag-quiet'>{entry.batchCount} sends grouped</span>}
						</div>}
					<EntryDetails entry={entry} changes={changes} useRoles={useRoles} />
					{onOpenTarget && entry.action !== 'delete' && !(entry.batchDocIds?.length > 1) &&
						<Button basic compact size='small' className='ch-open-target' onClick={() => onOpenTarget(entry)}>
							Open {COLLECTION_NOUN[entry.collection]}
						</Button>}
				</div>}
		</li>
	);
}

/** The Edits / Notifications / Replies toggles */
export function KindToggles({ kinds, onChange, counts, only }) {
	const options = KINDS.filter(k => !only || only.includes(k.key));
	return (
		<div className='ch-toggles' role='group' aria-label='Show changes'>
			{options.map(k => {
				const active = kinds.includes(k.key);
				return (
					<button
						key={k.key}
						type='button'
						aria-pressed={active}
						className={`ch-toggle ch-kind-${k.key} ${active ? 'ch-toggle-on' : ''}`}
						onClick={() => onChange(active ? kinds.filter(x => x !== k.key) : [...kinds, k.key])}
					>
						<Icon name={active ? 'check square' : 'square outline'} />
						{k.label}{counts && counts[k.key] !== undefined ? ` (${counts[k.key]})` : ''}
					</button>
				);
			})}
		</div>
	);
}

/**
 * props: entries, kinds (selected kind keys), showTarget (dashboard: say which record), useRoles (Services: label
 * changes by role), onOpenTarget(entry), loading, error, emptyText, hasMore + onLoadMore, loadingMore
 */
export default function ChangeHistory(props) {
	const { entries = [], kinds, showTarget, useRoles, onOpenTarget, loading, error, emptyText, hasMore, onLoadMore, loadingMore } = props;

	const days = useMemo(() => {
		const out = [];
		prepareEntries(entries, { acrossRecords: !!showTarget }).filter(e => isEntryVisible(e, kinds)).forEach(e => {
			const d = new Date(e.time);
			const key = format(d, 'yyyy-MM-dd');
			let day = out[out.length - 1];
			if (!day || day.key !== key) out.push(day = { key, label: dayLabel(d), entries: [] });
			day.entries.push(e);
		});
		return out;
	}, [entries, kinds, showTarget]);

	if (loading) return <div className='ch-status'><Loader active inline size='small' /> Loading history…</div>;
	if (error) return <div className='ch-status ch-error'><Icon name='warning circle' />Couldn't load the history: {String(error)}</div>;

	return (
		<div className='ch'>
			{days.length === 0 && <div className='ch-status'>{emptyText ?? 'No changes yet.'}</div>}
			{days.map(day => (
				<div key={day.key} className='ch-day'>
					<h4 className='ch-day-title'>{day.label}</h4>
					<ul className='ch-list'>
						{day.entries.map(e => <Entry key={e.id} entry={e} kinds={kinds} showTarget={showTarget} useRoles={useRoles} onOpenTarget={onOpenTarget} />)}
					</ul>
				</div>
			))}
			{hasMore &&
				<div className='ch-more'>
					<Button basic compact loading={loadingMore} disabled={loadingMore} onClick={onLoadMore}>Show older changes</Button>
				</div>}
		</div>
	);
}
